import { useEffect, useMemo, useRef, useState } from 'react';
import type { AiChange, ServerMessage } from '@roll1/shared';
import type { AiMarkKind } from '../components/ai/AiMark';

/**
 * AU-8 — what the AI added / changed, as marks on things of Studio: the key is «beat:<id>» or «journal:<id>» (a map or a
 * sheet likewise). A mark goes only when the GM has OPENED that very thing and left it again (never on a clock: a thing
 * not opened keeps its mark), and a change that was undone loses its mark. Kept on the client (not across a reload).
 */
interface MarkSource { readonly key: string; readonly mark: AiMarkKind; /** The thing whose leaving also clears this mark (a fork lit inside its beat). */ readonly owner?: string }

const keysOf = (ch: AiChange): MarkSource[] => {
  const e = ch.entity;
  // A fork the AI added is lit on its own row too; the beat it hangs on is «changed».
  if (e.kind === 'outcome' && ch.op === 'create') return [{ key: `outcome:${e.id}`, mark: 'added', ...(e.parentId !== undefined ? { owner: `beat:${e.parentId}` } : {}) }, ...(e.parentId !== undefined ? [{ key: `beat:${e.parentId}`, mark: 'changed' as const }] : [])];
  const one = keyOf(ch);
  return one === null ? [] : [one];
};

const keyOf = (ch: AiChange): MarkSource | null => {
  const e = ch.entity;
  switch (e.kind) {
    case 'beat': return { key: `beat:${e.id}`, mark: ch.op === 'create' ? 'added' : 'changed' };
    case 'journal': return { key: `journal:${e.id}`, mark: ch.op === 'create' ? 'added' : 'changed' };
    case 'page': return { key: `page:${e.id}`, mark: 'added' };
    case 'sheet': return { key: `sheet:${e.id}`, mark: 'added' };
    case 'outcome': return e.parentId !== undefined ? { key: `beat:${e.parentId}`, mark: 'changed' } : null;
    case 'objective': return e.parentId !== undefined ? { key: `journal:${e.parentId}`, mark: 'changed' } : null;
    default: return null;
  }
};

export function useAiMarks(
  onMessage: (handler: (msg: ServerMessage) => void) => () => void,
  /** The things the GM has open right now («beat:<id>», «journal:<id>», …). */
  openKeys: readonly string[],
  /** Which open thing a mark key lives in (a note lit inside its beat): leaving that thing clears it. */
  ownerOf?: (key: string) => string | undefined,
): ReadonlyMap<string, AiMarkKind> {
  const [sources, setSources] = useState<ReadonlyMap<string, MarkSource>>(new Map());
  // Changes whose mark is gone for good (their thing was opened and left): a re-sent card does not bring it back.
  const cleared = useRef(new Set<string>());

  useEffect(() => onMessage((msg: ServerMessage) => {
    if (msg.type !== 'ai:turn-changes') return;
    setSources((prev) => {
      const next = new Map(prev);
      for (const ch of msg.changes) {
        if (ch.undone === true || ch.kept === true) { for (const src of keysOf(ch)) next.delete(`${ch.id}|${src.key}`); continue; }
        if (cleared.current.has(ch.id)) continue;
        // one change may light several things: the map is keyed by change id + thing
        for (const src of keysOf(ch)) next.set(`${ch.id}|${src.key}`, src);
      }
      return next;
    });
  }), [onMessage]);

  // The thing that was open and is not any more: its marks go.
  const prevOpen = useRef<readonly string[]>([]);
  useEffect(() => {
    const left = prevOpen.current.filter((k) => !openKeys.includes(k));
    prevOpen.current = openKeys;
    if (left.length === 0) return;
    setSources((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, src] of prev) {
        if (!left.includes(src.key) && !left.includes(src.owner ?? '') && !left.includes(ownerOf?.(src.key) ?? '')) continue;
        cleared.current.add(id);
        next.delete(id);
        changed = true;
      }
      return changed ? next : prev;
    });
    // openKeys is rebuilt every render; its content is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKeys.join('|')]);

  return useMemo(() => {
    const out = new Map<string, AiMarkKind>();
    for (const src of sources.values()) {
      if (out.get(src.key) !== 'added') out.set(src.key, src.mark);
    }
    return out;
  }, [sources]);
}
